import React from 'react';
import { TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, Grid } from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import BusinessInfoGeneratorSection from './BusinessInfoGeneratorSection';

interface BasicInfoFieldsProps {
  formData: {
    business_name: string;
    business_type_id: number | null;
    website_url: string;
    description: string;
  };
  onChange: (field: string, value: any) => void;
  businessTypes: Array<{ id: number; name: string; display_name?: string }>;
  loadingBusinessTypes: boolean;
  isValid: boolean;
  websiteUrl?: string;
  onWebsiteChange?: (value: string) => void;
  onGenerateBusinessInfo?: () => void;
  generating?: boolean;
  generationError?: string;
  disabled?: boolean;
  quotaMessage?: string | null;
}

const BasicInfoFields: React.FC<BasicInfoFieldsProps> = ({
  formData,
  onChange,
  businessTypes,
  loadingBusinessTypes,
  isValid,
  websiteUrl,
  onWebsiteChange,
  onGenerateBusinessInfo,
  generating,
  generationError,
  disabled,
  quotaMessage,
}) => {
  const { t } = useTranslation('profile');
  
  return (
    <>
      {/* Business Name and Type - Top Row */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label={t('businessProfile.basicInfo.businessName')}
          value={formData.business_name}
          onChange={(e) => onChange('business_name', e.target.value)}
          required
          error={!isValid && !(formData.business_name && formData.business_name.trim().length >= 2)}
          helperText={!isValid && !(formData.business_name && formData.business_name.trim().length >= 2) ? t('businessProfile.basicInfo.businessNameRequired') : ''}
          InputProps={{
            startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth required error={!isValid && (formData.business_type_id === null || formData.business_type_id === '' || formData.business_type_id === undefined)}>
          <InputLabel shrink={!!formData.business_type_id}>{t('businessProfile.basicInfo.businessType')}</InputLabel>
          <Select
            value={formData.business_type_id || ''}
            onChange={(e) => onChange('business_type_id', e.target.value)}
            disabled={loadingBusinessTypes}
            label={t('businessProfile.basicInfo.businessType')}
            notched
          >
            {loadingBusinessTypes ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('businessProfile.basicInfo.loadingTypes')}
              </MenuItem>
            ) : (
              businessTypes?.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.display_name || type.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Grid>

      {/* Website URL Section - Moved above Description */}
      {websiteUrl !== undefined && onWebsiteChange && onGenerateBusinessInfo && (
        <Grid size={12}>
          <BusinessInfoGeneratorSection
            websiteUrl={websiteUrl}
            onChange={onWebsiteChange}
            onGenerate={onGenerateBusinessInfo}
            generating={generating || false}
            error={generationError || ''}
            disabled={disabled}
            quotaMessage={quotaMessage}
          />
        </Grid>
      )}

      {/* Description - Full Width */}
      <Grid size={12}>
        <TextField
          fullWidth
          label={t('businessProfile.basicInfo.businessDescription')}
          multiline
          rows={3}
          value={formData.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder={t('businessProfile.basicInfo.descriptionPlaceholder')}
          required
          error={!isValid && !(formData.description && formData.description.trim().length >= 3)}
          helperText={!isValid && !(formData.description && formData.description.trim().length >= 3) ? t('businessProfile.basicInfo.descriptionRequired') : ''}
        />
      </Grid>
    </>
  );
};

export default BasicInfoFields;

